import React from 'react';
import {motion} from 'framer-motion';
import {
    Baby,
    Ban,
    ChevronRight,
    Cpu,
    Gauge,
    Lock,
    Plug,
    ShieldCheck,
    Tv,
    Wifi,
} from 'lucide-react';
import {Link} from 'react-router-dom';
import {useSeo} from '../hooks/useSeo';
import miniPc from '../assets/mini_pc.webp';

const benefits = [
    {
        icon: Ban,
        title: 'Blokada reklam w całej sieci domowej',
        desc: 'MikroPC filtruje reklamy, bannery i wyskakujące okienka dla każdego urządzenia w Twoim Wi-Fi, bez instalowania żadnych dodatków czy wtyczek w przeglądarkach.',
    },
    {
        icon: Tv,
        title: 'Blokowanie reklam na Smart TV',
        desc: 'Koniec z uciążliwymi reklamami na telewizorze. Urządzenie blokuje serwery reklamowe bezpośrednio na poziomie DNS routera.',
    },
    {
        icon: Baby,
        title: 'Bezpieczeństwo i ochrona dzieci',
        desc: 'Świetne zabezpieczenie dla najmłodszych. Blokujemy natrętne i nieodpowiednie reklamy, strony dla dorosłych oraz podejrzane domeny phishingowe.',
    },
    {
        icon: Wifi,
        title: 'Działa na każdym urządzeniu automatycznie',
        desc: 'Telefony (iPhone, Android), laptopy, tablety, telewizory czy konsole. Wystarczy, że urządzenie połączy się z Twoim Wi-Fi, a reklamy znikają same.',
    },
    {
        icon: Gauge,
        title: 'Szybszy internet i oszczędność danych',
        desc: 'Strony www ładują się odczuwalnie szybciej, ponieważ Twoje łącze nie marnuje transferu i czasu na pobieranie setek skryptów śledzących i materiałów wideo.',
    },
    {
        icon: Lock,
        title: '100% prywatności domowników',
        desc: 'Blokujemy trackery analityczne i telemetryczne korporacji reklamowych, które w tle profilują Twoją rodzinę i zbierają historię przeglądania.',
    },
    {
        icon: Cpu,
        title: 'Cichy, miniaturowy i energooszczędny',
        desc: 'Mikrokomputer pobiera zaledwie 3-5W prądu (kilka złotych rocznie). Działa bezgłośnie 24/7, mieszcząc się na dłoni obok routera.',
    },
    {
        icon: Plug,
        title: 'Konfiguracja pod klucz lub Plug&Play',
        desc: 'Dostarczamy w pełni zaprogramowane urządzenie. Podłączamy je na miejscu w Mielcu i okolicach do 250 km lub wysyłamy gotowe do wpięcia w kabel LAN.',
    },
];

const steps = [
    {
        title: 'Dobór i przygotowanie',
        text: 'Programujemy mikroPC, wgrywamy najświeższe bazy blokujące i konfigurujemy reguły pod Twoją sieć.',
    },
    {
        title: 'Podłączenie do routera',
        text: 'Wystarczy podpiąć urządzenie kablem sieciowym do routera w Twoim domu w Mielcu lub okolicach.',
    },
    {
        title: 'Aktywacja blokady',
        text: 'Router kieruje zapytania przez mikroPC - od tego momentu cała sieć Wi-Fi jest wolna od reklam.',
    },
    {
        title: 'Czysty internet',
        text: 'Cieszysz się bezpiecznym, błyskawicznym internetem bez banerów na telefonach i telewizorach.',
    },
];

export function BlokadaReklamPage() {
    useSeo({
        title: 'Anty-reklama na sieć Wi-Fi w domu | Blokada reklam Mielec i do 250 km | kamerymielec.pl',
        description:
            'Pozbądź się reklam w domowej sieci Wi-Fi raz na zawsze! Konfiguracja mikroPC, który blokuje reklamy na Smart TV, telefonach i laptopach. Ochrona dzieci, szybszy internet. Montaż w Mielcu i promieniu do 250 km.',
        canonicalPath: '/blokada-reklam',
        keywords:
            'anty-reklama na wifi, blokada reklam wifi dom, blokowanie reklam smart tv mielec, mikroPC blokujący reklamy, adblock dla całej sieci, bezpieczny internet dla dzieci, kontrola rodzicielska router, monitoring mielec, kamery mielec',
        breadcrumbs: [
            {name: 'Strona główna', path: '/'},
            {name: 'Blokada reklam w sieci', path: '/blokada-reklam'},
        ],
        jsonLd: [
            {
                '@context': 'https://schema.org',
                '@type': 'Service',
                name: 'Anty-reklama i blokada reklam w sieci Wi-Fi (mikroPC)',
                serviceType: 'Konfiguracja sprzętowej blokady reklam w sieci domowej',
                description:
                    'Dostawa i konfiguracja dedykowanego mikrokomputera filtrującego ruch sieciowy. Blokuje reklamy, trackery i niepożądane treści na Smart TV, smartfonach i komputerach w całym domu.',
                areaServed: [
                    {'@type': 'City', name: 'Mielec'},
                    {'@type': 'City', name: 'Rzeszów'},
                    {'@type': 'City', name: 'Tarnów'},
                    {'@type': 'City', name: 'Dębica'},
                    {'@type': 'City', name: 'Stalowa Wola'},
                    {'@type': 'City', name: 'Tarnobrzeg'},
                    {'@type': 'City', name: 'Kolbuszowa'},
                    {'@type': 'City', name: 'Sandomierz'},
                ],
                provider: {
                    '@type': 'LocalBusiness',
                    name: 'Kamery Mielec - ONEXO',
                    url: 'https://kamerymielec.pl/',
                    telephone: '+48664896662',
                },
                url: 'https://kamerymielec.pl/blokada-reklam',
                inLanguage: 'pl-PL',
            },
            {
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: [
                    {
                        '@type': 'Question',
                        name: 'Czy anty-reklama działa na wszystkich urządzeniach w domowym Wi-Fi?',
                        acceptedAnswer: {
                            '@type': 'Answer',
                            text: 'Tak! Urządzenie mikroPC filtruje zapytania DNS na poziomie całego routera, dzięki czemu reklamy znikają z każdego połączonego sprzętu: Smart TV, telefonów iPhone i Android, tabletów, laptopów i konsol.',
                        },
                    },
                    {
                        '@type': 'Question',
                        name: 'Czy muszę instalować cokolwiek na telefonie lub telewizorze?',
                        acceptedAnswer: {
                            '@type': 'Answer',
                            text: 'Nie. Wystarczy podłączyć mikrokomputer do routera domowego. Zero wtyczek, zero instalowania aplikacji na urządzeniach domowników.',
                        },
                    },
                    {
                        '@type': 'Question',
                        name: 'Czy to dobre zabezpieczenie dla dzieci?',
                        acceptedAnswer: {
                            '@type': 'Answer',
                            text: 'Tak. Blokujemy natrętne i nieodpowiednie reklamy, strony hazardowe, treści dla dorosłych oraz podejrzane domeny, dzięki czemu dzieci korzystają z internetu znacznie bezpieczniej.',
                        },
                    },
                ],
            },
        ],
    });

    return (
        <div className="w-full py-12 md:py-20 px-5 bg-background relative overflow-hidden">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[min(800px,150vw)] h-[min(800px,150vw)] rounded-full bg-primary/[0.04] dark:bg-primary/[0.07] blur-[120px]"/>
            </div>

            <div className="relative max-w-6xl mx-auto">
                <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center mb-16">
                    <motion.div
                        initial={{opacity: 0, y: 30}}
                        animate={{opacity: 1, y: 0}}
                        transition={{duration: 0.6}}
                        className="text-center lg:text-left"
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs md:text-sm font-semibold mb-4">
                            <Ban size={14}/> Anty-reklama Wi-Fi - Mielec i promień do 250 km
                        </div>
                        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mb-4 leading-tight">
                            Anty-reklama na sieć Wi-Fi -{' '}
                            <span className="gradient-text">pozbądź się reklam w domu!</span>
                        </h1>
                        <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-6">
                            Dostarczamy i konfigurujemy gotowy mikrokomputer (mikroPC), który
                            blokuje reklamy na każdym urządzeniu połączonym z Twoją
                            domową siecią Wi-Fi. Bez wtyczek, bez konfiguracji każdego telefonu z osobna. 
                            Czysty obraz na Smart TV i bezpieczny internet dla dzieci.
                        </p>
                        <div className="flex flex-wrap justify-center lg:justify-start gap-3">
                            <Link
                                to="/wycena"
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-white font-semibold hover:brightness-110 transition-all shadow-md shadow-primary/20"
                            >
                                Zamów wycenę do domu <ChevronRight size={18}/>
                            </Link>
                            <a
                                href="tel:+48664896662"
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border bg-card text-foreground font-semibold hover:bg-muted transition-all"
                            >
                                Zadzwoń: +48 664 896 662
                            </a>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{opacity: 0, scale: 0.95}}
                        animate={{opacity: 1, scale: 1}}
                        transition={{duration: 0.6, delay: 0.15}}
                        className="relative"
                    >
                        <div className="absolute -inset-4 bg-primary/[0.07] rounded-[2rem] blur-2xl"/>
                        <img
                            src={miniPc}
                            alt="MikroPC blokujący reklamy w sieci WiFi w domu, montaż Mielec i okolice"
                            loading="eager"
                            className="relative w-full h-auto rounded-3xl border border-border/60 shadow-xl object-cover"
                        />
                    </motion.div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
                    {benefits.map((b, i) => {
                        const Icon = b.icon;
                        return (
                            <motion.div
                                key={b.title}
                                initial={{opacity: 0, y: 20}}
                                whileInView={{opacity: 1, y: 0}}
                                viewport={{once: true}}
                                transition={{duration: 0.5, delay: i * 0.05}}
                                className="relative rounded-2xl border border-border p-5 md:p-6 flex flex-col gap-3 bg-card hover:border-primary/30 transition-all"
                            >
                                <div className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center bg-primary/10 text-primary">
                                    <Icon size={22}/>
                                </div>
                                <h3 className="font-semibold text-sm md:text-base text-foreground">{b.title}</h3>
                                <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                                    {b.desc}
                                </p>
                            </motion.div>
                        );
                    })}
                </div>

                <motion.div
                    initial={{opacity: 0, y: 20}}
                    whileInView={{opacity: 1, y: 0}}
                    viewport={{once: true}}
                    transition={{duration: 0.5}}
                    className="rounded-3xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 md:p-10 mb-16"
                >
                    <h2 className="text-2xl md:text-3xl font-bold mb-10 text-center text-foreground">
                        Jak wygląda uruchomienie anty-reklamy?
                    </h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
                        {steps.map((item, i) => (
                            <div key={item.title} className="relative flex flex-col gap-3">
                                <div className="flex items-center gap-3">
                                    <div className="shrink-0 w-11 h-11 rounded-2xl bg-primary text-white flex items-center justify-center font-bold shadow-lg shadow-primary/25">
                                        {i + 1}
                                    </div>
                                    {i < steps.length - 1 && (
                                        <div className="hidden lg:block flex-1 h-px bg-gradient-to-r from-primary/40 to-transparent"/>
                                    )}
                                </div>
                                <h3 className="font-semibold text-base text-foreground">{item.title}</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                    {item.text}
                                </p>
                            </div>
                        ))}
                    </div>
                </motion.div>

                <motion.div
                    initial={{opacity: 0, y: 20}}
                    whileInView={{opacity: 1, y: 0}}
                    viewport={{once: true}}
                    transition={{duration: 0.5}}
                    className="rounded-3xl bg-gradient-to-br from-primary via-primary-dark to-purple-900 p-8 md:p-12 text-white text-center shadow-xl shadow-primary/10"
                >
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/15 mb-5">
                        <ShieldCheck size={28}/>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold mb-3">
                        Chcesz czysty i bezpieczny internet bez reklam w całym domu?
                    </h2>
                    <p className="text-white/85 max-w-xl mx-auto mb-7 text-sm md:text-base leading-relaxed">
                        Dostarczamy i konfigurujemy gotowy mikroPC dla Twojej sieci Wi-Fi w Mielcu oraz w promieniu do 250 km. Skontaktuj się z nami i pozbądź się reklam raz na zawsze!
                    </p>
                    <Link
                        to="/wycena"
                        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-primary font-bold hover:bg-white/90 transition-all shadow-md shadow-black/15"
                    >
                        Zamów anty-reklamę na Wi-Fi <ChevronRight size={18}/>
                    </Link>
                </motion.div>
            </div>
        </div>
    );
}
